import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import rcnLogo from "@/assets/rcn-logo.png";

type State =
  | { kind: "loading" }
  | { kind: "ready"; url: string }
  | { kind: "paid" }
  | { kind: "error"; message: string };

const PayDues = () => {
  const [params] = useSearchParams();
  const appId = params.get("app");
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    document.title = "Pay Membership Dues | RCN";
    if (!appId) {
      setState({ kind: "error", message: "This payment link is missing information. Please contact RCN for a new link." });
      return;
    }
    (async () => {
      const { data, error } = await supabase.functions.invoke("create-membership-checkout", {
        body: {
          application_id: appId,
          public_link: true,
          success_url: `${window.location.origin}/auth`,
          cancel_url: `${window.location.origin}/pay-dues?app=${appId}`,
        },
      });
      let message = (data as any)?.error as string | undefined;
      if (error && !message) {
        try {
          message = (await (error as any).context?.json())?.error;
        } catch { /* ignore */ }
      }
      if ((data as any)?.already_paid) return setState({ kind: "paid" });
      const url = (data as any)?.url;
      if (url) {
        setState({ kind: "ready", url });
        window.location.href = url;
        return;
      }
      setState({ kind: "error", message: message || "We couldn't open the payment page. Please try again or contact RCN." });
    })();
  }, [appId]);

  return (
    <main className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md text-center space-y-6 rounded-lg border bg-card p-8 shadow-sm">
        <img src={rcnLogo} alt="RCN" className="h-14 mx-auto" />
        {state.kind === "loading" && (
          <>
            <Loader2 className="h-8 w-8 mx-auto animate-spin text-primary" />
            <p className="text-muted-foreground">Preparing your secure payment page…</p>
          </>
        )}
        {state.kind === "ready" && (
          <>
            <Loader2 className="h-8 w-8 mx-auto animate-spin text-primary" />
            <p className="text-muted-foreground">Taking you to checkout…</p>
            <Button asChild className="w-full"><a href={state.url}>Proceed to payment</a></Button>
          </>
        )}
        {state.kind === "paid" && (
          <>
            <CheckCircle2 className="h-10 w-10 mx-auto text-primary" />
            <h1 className="text-xl font-semibold">This membership is already paid</h1>
            <p className="text-muted-foreground">Thank you! No further payment is needed.</p>
            <Button asChild variant="outline"><Link to="/auth">Go to member login</Link></Button>
          </>
        )}
        {state.kind === "error" && (
          <>
            <AlertCircle className="h-10 w-10 mx-auto text-destructive" />
            <p className="text-muted-foreground">{state.message}</p>
            <Button onClick={() => window.location.reload()}>Try again</Button>
          </>
        )}
      </div>
    </main>
  );
};

export default PayDues;
